import { useEffect, useCallback, useState } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import PipelineTracker from "../components/PipelineTracker";
import AcquireAgent from "../components/AcquireAgent";
import PipelineChatbot from "../components/PipelineChatbot";
import WorkflowAlertsPanel from "../components/WorkflowAlertsPanel";

import HomePage from "../pages/HomePage";
import DevConsolePage from "../pages/DevConsolePage";
import CRMPage from "../pages/CRMPage";
import QuickDealAnalysisPage from "../pages/QuickDealAnalysisPage";
import DealEntryPage from "../pages/DealEntryPage";
import DealLeadPage from "../pages/DealLeadPage";
import Module2Page from "../pages/Module2Page";
import Module3Page from "../pages/Module3Page";
import Module4Page from "../pages/Module4Page";
import Module5Page from "../pages/Module5Page";
import Module6Page from "../pages/Module6Page";
import Module7Page from "../pages/Module7Page";
import Module8Page from "../pages/Module8Page";
import Module9Page from "../pages/Module9Page";
import Module10Page from "../pages/Module10Page";
import Module11Page from "../pages/Module11Page";
import Module12Page from "../pages/Module12Page";
import Module13Page from "../pages/Module13Page";
import Module14Page from "../pages/Module14Page";
import Module15Page from "../pages/Module15Page";
import Module16Page from "../pages/Module16Page";
import Module17Page from "../pages/Module17Page";
import DealDashboardPage from "../pages/DealDashboardPage";
import OrganizationProfilePage from "../pages/OrganizationProfilePage";
import AdminDashboardPage from "../pages/AdminDashboardPage";

import useDealStore from "../store/dealStore";
import useAuthStore from "../store/authStore";
import "../styles/market-intel-panel.css";
import {
  recordActivity,
  recordDealStart,
  recordModuleActivity,
  checkIdleNotifications,
} from "../services/idleNotificationService";

import "../styles/layout.css";

// Maps actual module number → display number shown in sidebar
const MODULE_DISPLAY_NUMBER = {
  1: 1, 3: 2, 4: 3, 5: 4, 6: 5, 7: 6, 8: 7, 9: 8, 10: 9,
  11: 10, 12: 11, 13: 12, 14: 13, 15: 14, 16: 15, 17: 16,
};

function MainLayout() {
  const { activeModule, dealState, marketIntelligenceOpen, setMarketIntelligenceOpen, navigateToModule } = useDealStore();
  const { user } = useAuthStore();
  const [mobileModulesOpen, setMobileModulesOpen] = useState(false);

  // Redirect admin users to their administrative dashboard, and keep standard users off of it
  useEffect(() => {
    if (user) {
      const isUserAdmin = ['admin', 'host'].includes(user.role);
      if (isUserAdmin && activeModule === 'dashboard') {
        navigateToModule('admin-dashboard');
      } else if (!isUserAdmin && activeModule === 'admin-dashboard') {
        navigateToModule('dashboard');
      }
    }
  }, [user, activeModule, navigateToModule]);

  // ── Track user activity & module advances (non-admin only) ────────────────
  const isAdmin = user && ['admin', 'host'].includes(user.role);

  useEffect(() => {
    if (isAdmin) return;
    recordActivity();
    if (dealState.dealId) {
      recordDealStart(dealState.dealId);
      if (activeModule > 0) recordModuleActivity();
    }
  }, [activeModule, dealState.dealId, isAdmin]);

  // ── Check idle thresholds on mount and when the tab regains focus ──────────
  const runIdleCheck = useCallback(() => {
    if (isAdmin || !user?.email || !dealState) return;
    checkIdleNotifications(dealState, user.email);
  }, [dealState, user?.email, isAdmin]);

  useEffect(() => {
    if (isAdmin) return;
    runIdleCheck();
    window.addEventListener('focus', runIdleCheck);
    return () => window.removeEventListener('focus', runIdleCheck);
  }, [runIdleCheck, isAdmin]);

  useEffect(() => {
    setMobileModulesOpen(false);
  }, [activeModule]);

  const renderPage = () => {
    // Market Intelligence takes over the full workspace when open
    if (marketIntelligenceOpen) {
      return (
        <div className="mi-fullpage">
          <div className="mi-panel-topbar">
            <div className="mi-panel-title">
              <span className="mi-panel-icon">📊</span>
              <span>Market Intelligence</span>
              <span className="mi-panel-always-badge">Always Accessible</span>
            </div>
            <button
              className="mi-panel-close"
              onClick={() => setMarketIntelligenceOpen(false)}
            >
              ✕
            </button>
          </div>
          <div className="mi-panel-content">
            <Module2Page />
          </div>
        </div>
      );
    }

    switch (activeModule) {
      case 'quick-deal':
        return <QuickDealAnalysisPage />;
      case 'dev-console':
        return <DevConsolePage />;
      case 'crm':
        return <CRMPage />;
      case 'org-profile':
        return <OrganizationProfilePage />;
      case 'admin-dashboard':
        return <AdminDashboardPage />;
      case 'dashboard':
        return <DealDashboardPage />;
      case 'entry':
        return <DealEntryPage />;
      case 0:
        return <HomePage />;
      case 1:
        return <DealLeadPage />;
      case 2:
        return <Module2Page />;
      case 3:
        return <Module3Page />;
      case 4:
        return <Module4Page />;
      case 5:
        return <Module5Page />;
      case 6:
        return <Module6Page />;
      case 7:
        return <Module7Page />;
      case 8:
        return <Module8Page />;
      case 9:
        return <Module9Page />;
      case 10:
        return <Module10Page />;
      case 11:
        return <Module11Page />;
      case 12:
        return <Module12Page />;
      case 13:
        return <Module13Page />;
      case 14:
        return <Module14Page />;
      case 15:
        return <Module15Page />;
      case 16:
        return <Module16Page />;
      case 17:
        return <Module17Page />;
      default:
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: '16px',
            color: '#6b7280'
          }}>
            <div style={{ fontSize: '48px' }}>🚧</div>
            <h2 style={{ color: '#111827', margin: 0 }}>Module {activeModule} Coming Soon</h2>
            <p style={{ margin: 0 }}>This module is under construction.</p>
          </div>
        );
    }
  };

  // ── Admin-only layout: clean sidebar + dashboard, no user chrome ──────────
  if (isAdmin) {
    return (
      <div className="layout">
        <Sidebar />
        <div className="main-content">
          <div className="workspace" style={{ paddingTop: 0 }}>
            {renderPage()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layout">
      <Sidebar />
      <div className="main-content">
        <Header />
        <button
          className="mobile-module-menu-btn"
          onClick={() => setMobileModulesOpen(true)}
          aria-label="Open modules menu"
          title="Open modules"
        >
          <span>•••</span>
        </button>
        <div className="workspace">
          {Number.isInteger(activeModule) && activeModule > 0 && activeModule !== 3 && activeModule !== 'quick-deal' && (
            <WorkflowAlertsPanel
              title={`Module ${MODULE_DISPLAY_NUMBER[activeModule] ?? activeModule} Alerts`}
              moduleNumber={activeModule}
              compact
              limit={2}
            />
          )}
          {renderPage()}
        </div>
        <PipelineTracker />
        <PipelineChatbot />
      </div>
      {mobileModulesOpen && (
        <div className="mobile-module-drawer-shell">
          <button
            className="mobile-module-drawer-backdrop"
            onClick={() => setMobileModulesOpen(false)}
            aria-label="Close modules menu"
          />
          <aside className="mobile-module-drawer" aria-label="Modules menu">
            <button
              className="mobile-module-drawer-close"
              onClick={() => setMobileModulesOpen(false)}
              aria-label="Close modules menu"
            >
              ←
            </button>
            <Sidebar />
          </aside>
        </div>
      )}
      <AcquireAgent />
    </div>
  );
}

export default MainLayout;
